/* eslint-disable no-param-reassign */
import { useState, useEffect, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, Paper, Stack } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { SelectionForm } from 'src/modules/groupMessageEmail/components/selectionForm';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { updateGroupMessage, getGroupMessageById, getSelectionById } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { ControlledFileDropZone } from 'src/shared/components/form/fileDropZone';
import { DocumentFile } from 'src/shared/models/documentFile';
import { LoadingButton } from '@mui/lab';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { removeBase64Images } from 'src/shared/functions/removeBase64Images';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Pageview } from '@mui/icons-material';
import { GroupMessageRequestPut } from '../models/groupMessageRequestPut';
import { FixedSelection } from '../models/fixedSelection';
import { RemovedImagesDialog } from '../components/removedImagesDialog';
import { HandleGroupMessageMenu } from '../components/handleGroupMessageMenu';
import { GroupMessageResponse } from '../models/groupMessageResponse';
import { GroupMessageEmailStatusMessage } from '../components/groupMessageEmailStatusMessage';
import { GroupMessageEmailEditPreviewModal } from '../components/groupMessageEmailEditPreviewModal';
import { GroupMessageIsDeletedStatusMessage } from '../components/groupMessageIsDeletedStatusMessage';

const GroupMessageEmailEditPage = () => {
  const { t } = useTranslation(['groupMessage', 'common']);
  const params = useParams();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(true);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);
  const [documentFiles, setDocumentFiles] = useState<DocumentFile[] | undefined>();
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [sendMode, setSendMode] = useState<boolean>(false);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [dataToSave, setDataToSave] = useState<GroupMessageRequestPut>();
  const [groupMessageResponse, setGroupMessageResponse] = useState<GroupMessageResponse>();

  const requiredMessage = t('common:validationMessages.required');

  const GroupMessageRequestPutSchema: Schema<GroupMessageRequestPut> = yup.object().shape({
    circular_id: yup.number().required(requiredMessage),
    product_id: yup.number().optional(),
    selection_id: yup.number().required(requiredMessage),
    circularName: yup.string().optional().nullable(),
    selectionName: yup.string().optional().nullable(),
    recipients: yup
      .string()
      .trim()
      .matches(
        /^(([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+([;.](([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+)*$/,
        {
          message: t('common:validationMessages.multipleEmail'),
          excludeEmptyString: true,
        },
      ),
    subject: yup
      .string()
      .required(requiredMessage)
      .max(50, t('common:validationMessages.maxLength', { count: 50 })),
    body: yup.string().required(requiredMessage),
    plainText: yup.string().optional().nullable(),
    isHTML: yup.boolean().required(),
    useLinkCounter: yup.boolean().optional(),
    sendDate: yup.string().optional().nullable(),
    sender: yup.string().email().required(requiredMessage),
    documentFiles: yup.mixed().optional(),
    documentIds: yup.string().optional().nullable(),
    localAss_id: yup.number().optional(),
    countyAss_id: yup.number().optional(),
    senderTitle: yup.string().required(),
    selectionType: yup.number().optional(),
    speedFormValues: yup.string().optional(),
    speedFormText: yup.string().optional(),
    files: yup.mixed<File[]>().optional().nullable().notRequired(),
    yvMultiCustName: yup.string().optional(),
    created: yup.string().optional(),
    send: yup.boolean().optional(),
  });

  const formMethods = useForm<GroupMessageRequestPut>({
    resolver: yupResolver(GroupMessageRequestPutSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      sender: '',
      senderTitle: '',
      documentIds: '',
      body: '',
      selection_id: 0,
      send: false,
      speedFormText: '',
      speedFormValues: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    trigger,
    getValues,
    reset,
    setValue,
  } = formMethods;

  const fetchData = useCallback(async () => {
    try {
      const result = await getGroupMessageById(params.id);
      setLoading(false);
      ConsoleHelper.log('fetch', result.data);

      result.data.selection_id = result.data.selection_id ?? '';
      result.data.recipients = result.data.recipients ?? '';
      reset(result.data);
      setGroupMessageResponse(result.data);
      setDocumentFiles(result.data.documentFiles);
      setDocumentIds(result.data.documentIds?.split(',') || []);
      if (result.data.speedFormValues) {
        setFixedSelection({
          name: result.data.speedFormText,
          value: result.data.speedFormValues,
        } as FixedSelection);
      }
      if (result.data.selection_id !== 0) {
        const sel = await getSelectionById(result.data.selection_id);
        setSelection(sel.data as Selection);
        // ConsoleHelper.log(result.data.selection_id, sel.data);
      }
    } catch (e) {
      ConsoleHelper.error('getSelectionById error:', e);
    }
  }, [params.id, reset]);

  useEffect(() => {
    if (isMounted() && params.id) {
      fetchData();
    }
  }, [fetchData, isMounted, params.id]);

  const goToOverview = useCallback(() => {
    navigate(RoutesEnum.GroupMessageEmailOverview);
  }, [navigate]);

  const postData = useCallback(
    async (data) => {
      ConsoleHelper.log('postData', data);
      try {
        const res = await updateGroupMessage(data);
        if (isMounted()) {
          ConsoleHelper.log('res updateGroupMessage', res);
          if (data.send) toast.success(<>{t('groupMessage:edit.sendSuccess')}</>);
          else toast.success(<>{t('groupMessage:edit.saveSuccess')}</>);
          goToOverview();
        }
      } catch (e) {
        if (data.send) toast.warning(<>{t('groupMessage:edit.sendError')}</>);
        else toast.warning(<>{t('groupMessage:edit.saveError')}</>);
        ConsoleHelper.log('saveFormDataError:', e);
      }
    },
    [goToOverview, isMounted, t],
  );

  const saveFormData = useCallback(
    async (data: GroupMessageRequestPut) => {
      data.includeRecipientsThatHasUnsubscribedFromEmail = false;
      data.speedFormText = data.speedFormText ?? '.';
      data.speedFormValues = data.speedFormValues ?? '.';
      data.send = sendMode;
      data.documentIds = documentIds.join(',');
      const { modifiedContent, strippedImages } = removeBase64Images(data.body);
      // Spara arrayen med bilder i statet
      setRemovedImages(strippedImages);
      if (strippedImages.length > 0) {
        data.body = modifiedContent;
        ConsoleHelper.log('modifiedContent', modifiedContent);
        setValue('body', modifiedContent);
        setDataToSave(data);
      } else {
        postData(data);
      }
    },
    [documentIds, postData, sendMode, setValue],
  );

  const handleConfirmImageRemoval = () => {
    postData(dataToSave);
  };

  const onSubmit = async (data: GroupMessageRequestPut) => {
    await trigger();
    if (Object.keys(errors).length === 0) {
      try {
        await saveFormData({ ...data });
      } catch (e) {
        toast.error('Misslyckades');
      }
    }
  };

  const onDocumentFileRemove = (fileId: string) => {
    setDocumentIds(documentIds.filter((item) => item.toString() !== fileId));
    setDocumentFiles(documentFiles?.filter((item) => item.document_id.toString() !== fileId));
  };

  const textEditorInitalized = useCallback(() => {
    return null;
  }, []);

  const returnToOverview = () => {
    navigate(RoutesEnum.GroupMessageEmailOverview);
  };

  const handleCopy = () => {
    returnToOverview();
  };

  const messageIsSent = !!(groupMessageResponse && !!groupMessageResponse?.sendDate);
  const isDeleted = groupMessageResponse?.deletedAt != null;

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* <Prompt when={isDirty && !isSubmitted && !isSubmitting} message={t('common:statusMessages.leaveFormWarning')} /> */}
          <GridWrapper fixed>
            <Grid item xs='auto' style={{ display: 'flex' }}>
              <MainContentHeader title={t('groupMessage:edit.header')} />
            </Grid>
            {params.id && (
              <Grid item xs style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <HandleGroupMessageMenu
                  disabled={false}
                  groupMessageId={params.id}
                  callback={handleCopy}
                  deletedAt={getValues('deletedAt') ?? null}
                  messageIsSent={messageIsSent}
                  refetchCallback={(val: boolean) => {
                    if (val === true) {
                      fetchData();
                    }
                  }}
                />
              </Grid>
            )}
            {removedImages && removedImages.length > 0 && (
              <RemovedImagesDialog
                removedImages={removedImages}
                open
                handleClose={() => setRemovedImages([])}
                confirmImageRemoval={handleConfirmImageRemoval}
              />
            )}
            <GroupMessageEmailEditPreviewModal
              id={params.id ? +params.id : 0}
              documentIds={documentIds}
              modalOpen={showPreviewModal}
              onClose={() => setShowPreviewModal(false)}
              onSave={() => {
                setShowPreviewModal(false);
                returnToOverview();
              }}
            />
            {messageIsSent && groupMessageResponse && (
              <GroupMessageEmailStatusMessage groupMessageEmailResponse={groupMessageResponse} callback={() => ConsoleHelper.log('test')} />
            )}
            {isDeleted && <GroupMessageIsDeletedStatusMessage deletedAt={getValues('deletedAt') || ''} deletedBy={getValues('deletedBy') || ''} />}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                }}
              >
                <GridWrapper>
                  <Grid item xs={12} lg={8}>
                    <GridWrapper>
                      <Grid item xs={12}>
                        <SelectionForm
                          disabled={messageIsSent || isDeleted}
                          selectionFromServer={selection}
                          fixedSelectionFromServer={fixedSelection}
                          onSelection={(x) => setSelection(x)}
                          onFixedSelection={(x) => setFixedSelection(x)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='subject'
                          required
                          label={t('groupMessage:formFields.subject')}
                          disabled={messageIsSent || isDeleted}
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextEditor
                          name='body'
                          disabled={messageIsSent || isDeleted}
                          initCallback={textEditorInitalized}
                          resizeAllowed
                          rows={50}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='sender'
                          required
                          label={t('groupMessage:formFields.sender')}
                          disabled={messageIsSent || isDeleted}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='senderTitle'
                          required
                          label={t('groupMessage:formFields.senderTitle')}
                          disabled={messageIsSent || isDeleted}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='recipients'
                          disabled={messageIsSent || isDeleted}
                          label={t('groupMessage:formFields.recipients')}
                          helperText={t('groupMessage:formFields.recipients_helperText')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </GridWrapper>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <GridWrapper>
                      <Grid item xs={12}>
                        <ControlledFileDropZone
                          name='files'
                          multiple
                          showFileHeader
                          disabled={messageIsSent || isDeleted}
                          orientation='vertical'
                          documentFiles={documentFiles}
                          onDocumentFileRemove={onDocumentFileRemove}
                        />
                      </Grid>
                      {Object.keys(errors).length > 0 && (
                        <Grid item xs={12}>
                          <FormErrorSummary translationNamespace='groupMessage' errors={errors} />
                        </Grid>
                      )}
                    </GridWrapper>
                  </Grid>
                  {!isDeleted && (
                    <Grid item xs={12} textAlign='right' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Stack direction='row' spacing={1}>
                        <Button variant='outlined' type='button' onClick={returnToOverview}>
                          {t('common:button.cancel')}
                        </Button>
                        <Button
                          variant='outlined'
                          type='button'
                          disabled={Object.keys(errors).length > 0}
                          onClick={async () => {
                            await trigger();
                            if (isValid) setShowPreviewModal(true);
                          }}
                          startIcon={<Pageview aria-hidden />}
                        >
                          {t('common:button.preview')}
                        </Button>
                        <LoadingButton
                          loading={isSubmitting}
                          variant='contained'
                          type='submit'
                          disableElevation
                          onClick={() => setSendMode(false)}
                          disabled={Object.keys(errors).length > 0 || !!groupMessageResponse?.sendDate}
                        >
                          {t('common:button.save')}
                        </LoadingButton>
                        <Divider orientation='vertical' variant='middle' flexItem />
                        <LoadingButton
                          loading={isSubmitting}
                          variant='contained'
                          type='submit'
                          disableElevation
                          disabled={Object.keys(errors).length > 0 || !!groupMessageResponse?.sendDate || (!selection && !fixedSelection)}
                          onClick={() => setSendMode(true)}
                          endIcon={<SendIcon aria-hidden />}
                        >
                          {t('common:button.saveAndSend')}
                        </LoadingButton>
                      </Stack>
                    </Grid>
                  )}
                </GridWrapper>
              </Paper>
            </Grid>
          </GridWrapper>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};

export default GroupMessageEmailEditPage;
